import { MockRoute } from '../mockBackend';
import { PreviewDataset, PreviewEmployee, Proficiency } from '../../preview.types';

// Employee profile drill-in for the preview sandbox: GET /Profile?employeeId=, built from the
// synthetic fixture. UserProfile (src/data/users/UserProfile.ts) unconditionally constructs lists
// from three arrays — `employee.employeeAttributes`, `jobDefinition.jobAttributes`, `gapAttributes`
// — and reads `attributeSummaryDto` field-by-field, so all four MUST always be present or the
// profile dialog throws. Enum ordinals are index-based and must be exact:
//   • employeeAttributes[].validationRequestState = 2  → "approved"  (Abstract_Attribute.status)
//   • jobAttributes[].employeeAttributeToJobAttributeMetType = 1 (met, held) else 2 (gap)
//     (Abstract_Attribute.gapReason: [notSet, met, unmetMandatory, unmet]) — wrong value flags
//     every held competency as a gap.
//   • gapAttributes[].gapReason indexes a DIFFERENT enum — GapAttribute.reason: [ok, lowProficiency,
//     missing] (backend GapReason). 2 = missing. 0 would label every gap "OK" and break the Reason
//     filter, which only offers Low proficiency / Missing.
//
// Also empty-states the profile dialog's Training sub-tab (a side-effect of enabling the Training
// feature): /EmployeeTraining/GetTrainings, /Profile/Trainings, /Practical/participant return shapes
// whose consumed values are empty arrays → a clean "no records" tab, never a crash.

const PROFICIENCY_VALUE: Record<Proficiency, number> = { developing: 1, competent: 2, expert: 3 };
const MET = 1; // gapReason ordinal for a satisfied job competency
const GAP = 2; // gapReason ordinal for an unmet (mandatory) job competency
const APPROVED = 2; // validationRequestState ordinal
const MISSING = 2; // GapAttribute.reason ordinal — the competency isn't held at all
const REQUIRED = 1; // jobAttributeRequirementType ordinals (GapAttribute reads 1/2, else desirable)
const MANDATORY = 2;

function buildProfile(dataset: PreviewDataset, employeeId: string | null) {
  const defs = new Map(dataset.attributeDefinitions.map((d) => [d.id, d]));
  const jobs = new Map(dataset.jobs.map((j) => [j.id, j]));
  const employee: PreviewEmployee | undefined =
    dataset.employees.find((e) => e.id === employeeId) ?? dataset.employees[0];

  // Defensive: empty-but-valid profile if the fixture is somehow empty.
  if (!employee) {
    return {
      employee: {
        employeeId: employeeId ?? '',
        fullName: '',
        employeeAttributes: [],
        attributeSummaryDto: emptySummary(),
      },
      jobDefinition: { description: '', jobAttributes: [], regulationTasks: [] },
      gapAttributes: [],
      job: { department: '' },
    };
  }

  const job = jobs.get(employee.jobId ?? '');
  const mandatory = job?.mandatoryAttributeIds ?? [];
  const required = job?.requiredAttributeIds ?? [];
  const desirable = job?.desirableAttributeIds ?? [];
  const mustHave = [...mandatory, ...required]; // compliance set (drives the profile's gaps)
  const mandatorySet = new Set(mandatory);
  const heldById = new Map(employee.attributes.map((a) => [a.attributeDefinitionId, a]));
  const gaps = mustHave.filter((id) => !heldById.has(id));
  const metIn = (ids: string[]) => ids.filter((id) => heldById.has(id)).length;

  // Held competencies → employeeAttributes (all "approved").
  const employeeAttributes = employee.attributes.map((a) => {
    const def = defs.get(a.attributeDefinitionId);
    return {
      employeeAttributeId: `${employee.id}-${a.attributeDefinitionId}`,
      attributeDefinitionId: a.attributeDefinitionId,
      attributeDefinitionName: def?.name ?? a.attributeDefinitionId,
      attributeDefinitionCategory: def?.category ?? '',
      attributeDefinitionHasExpiryDate: !!a.expiresOn,
      attributeDefinitionHasProficiency: true,
      proficiencyValue: PROFICIENCY_VALUE[a.proficiency],
      expiryDate: a.expiresOn ?? null,
      lastUpdateDate: '2025-01-01T00:00:00.000Z',
      validationRequestState: APPROVED,
      validationRequest: null,
      isParent: false,
      attributeDefinitionChildren: [],
    };
  });

  // Job requirements → jobAttributes (met-type 1 if held, else 2). Mandatory vs Required is flagged
  // per competency so the profile mirrors the team overview's buckets.
  const jobAttributes = mustHave.map((id) => {
    const def = defs.get(id);
    const held = heldById.get(id);
    const isMandatory = mandatorySet.has(id);
    return {
      attributeDefinitionId: id,
      attributeDefinitionName: def?.name ?? id,
      attributeDefinitionCategory: def?.category ?? '',
      isParent: false,
      hasExpiryDate: !!held?.expiresOn,
      hasProficiency: true,
      required: !isMandatory,
      mandatory: isMandatory,
      proficiencyValue: held ? PROFICIENCY_VALUE[held.proficiency] : null,
      proficiencyMinimum: 2,
      employeeAttributeToJobAttributeMetType: held ? MET : GAP,
      isMinimum: false,
    };
  });

  // Gaps → gapAttributes. `mustHave` is mandatory + required, so the requirement type has to be
  // per-competency: GapAttribute's constructor derives `required`/`mandatory` from it AND freezes
  // `type` off them there. GapAttributeList.initAttributesLists later re-copies the two booleans
  // from the job attribute but never recomputes `type`, so a hardcoded value shows every gap as
  // Mandatory however the two are corrected afterwards.
  const gapAttributes = gaps.map((id) => {
    const def = defs.get(id);
    return {
      employeeAttributeId: '', // a missing competency has no EmployeeAttribute record
      attributeDefinitionId: id,
      attributeDefinitionName: def?.name ?? id,
      attributeDefinitionCategory: def?.category ?? '',
      jobAttributeRequirementType: mandatorySet.has(id) ? MANDATORY : REQUIRED,
      employeeAttributeProficiencyValue: null,
      jobAttributeProficiencyMinimum: 2,
      gapReason: MISSING,
      checklists: [],
    };
  });

  return {
    employee: {
      employeeId: employee.id,
      fullName: `${employee.firstName} ${employee.lastName}`,
      jobTitle: job?.title ?? '',
      email: null,
      location: employee.location,
      isOrganizationAdmin: false,
      isTeamLead: false,
      reportsTo: null,
      hasAGroupManagerApprover: false,
      hasProfilePicture: false,
      employeeAttributes,
      attributeSummaryDto: {
        totalNumberOfMandatoryAttributes: mandatory.length,
        numberOfMetMandatoryAttributes: metIn(mandatory),
        totalNumberOfRequiredAttributes: required.length,
        numberOfMetRequiredAttributes: metIn(required),
        totalNumberOfDesirableAttributes: desirable.length,
        numberOfMetDesirableAttributes: metIn(desirable),
      },
    },
    jobDefinition: {
      description: job?.description ?? `Synthetic role profile for ${job?.title ?? 'this role'}.`,
      jobAttributes,
      regulationTasks: [],
    },
    gapAttributes,
    job: { department: job?.department ?? 'Flight Operations' },
  };
}

// --- Competency activity history ------------------------------------------------------
//
// GET /EmployeeAttribute/GetActivityHistory/:employeeAttributeId — the feed shown when a competency
// is opened from a profile. Without it the panel mounts and renders EMPTY (AttributeRequestActivity
// assigns itself synchronously, so the UI can't tell a blocked fetch from "no history").
//
// The story is derived, not authored: every competency gets requested → approved, and one that
// expires also gets a renewal. Deriving it keeps all nine employees consistent for free; if a
// specific narrative is ever needed, add an optional `history` to PreviewEmployeeAttribute and
// prefer it here.
//
// `documents` MUST be an array — DocumentList's constructor iterates it unguarded. Every actor needs
// `employeeHasProfilePicture: false`, or the avatar fetch escapes to the fail-closed mock.
const DAY_MS = 24 * 60 * 60 * 1000;
const shiftDays = (iso: string, days: number) =>
  new Date(new Date(iso).getTime() + days * DAY_MS).toISOString();

/** Valid-but-empty: every array the feed iterates is present. */
const EMPTY_ACTIVITY_HISTORY = {
  activities: [],
  documents: [],
  checklists: [],
  trainingCourses: [],
};

function actor(employee: PreviewEmployee) {
  return {
    employeeId: employee.id,
    employeeFirstName: employee.firstName,
    employeeLastName: employee.lastName,
    employeeFullName: `${employee.firstName} ${employee.lastName}`,
    employeeHasProfilePicture: false,
  };
}

function buildActivityHistory(dataset: PreviewDataset, employeeAttributeId: string) {
  // Match on the composed id rather than splitting it — employee ids may themselves contain '-'.
  let owner: PreviewEmployee | undefined;
  let attribute: PreviewEmployee['attributes'][number] | undefined;
  for (const e of dataset.employees) {
    const found = e.attributes.find(
      (a) => `${e.id}-${a.attributeDefinitionId}` === employeeAttributeId
    );
    if (found) {
      owner = e;
      attribute = found;
      break;
    }
  }

  if (!owner || !attribute) return EMPTY_ACTIVITY_HISTORY;

  // The approver is another real person from the fixture, so the feed reads consistently with the
  // rest of the demo rather than inventing a name.
  const approver = dataset.employees.find((e) => e.id !== owner.id) ?? owner;

  // Anchor to the expiry so a renewing competency's dates line up with the badge the user just saw.
  // Everything is clamped to the past: these are things that HAVE happened, and a competency
  // expiring more than two years out would otherwise date its own creation in the future.
  const past = (iso: string) =>
    new Date(Math.min(new Date(iso).getTime(), Date.now())).toISOString();
  const addedOn = past(
    attribute.expiresOn ? shiftDays(attribute.expiresOn, -730) : '2023-03-14T09:00:00.000Z'
  );
  const acceptedOn = past(shiftDays(addedOn, 3));

  const activities = [
    {
      commentId: `${employeeAttributeId}-added`,
      createDate: addedOn,
      commentReason: 'commentReasons.competencyAdded',
      ...actor(owner),
      text: null,
      documentIds: [],
      checklistAssignmentIds: [],
    },
    {
      commentId: `${employeeAttributeId}-accepted`,
      createDate: acceptedOn,
      commentReason: 'commentReasons.competencyAccepted',
      ...actor(approver),
      text: null,
      documentIds: [],
      checklistAssignmentIds: [],
    },
  ];

  // Three-way, so the feed never contradicts the expiry badge the user just clicked:
  //   • no expiry            → the two rows above; nothing renews
  //   • expires in future    → a renewal (in Klayo a recurring competency renews by completing the
  //                            training again), clamped to the past so it is never a future event
  //   • already expired      → the expiry itself is the latest thing that happened
  if (attribute.expiresOn) {
    const expiry = new Date(attribute.expiresOn).getTime();
    const expired = expiry < Date.now();
    activities.push({
      commentId: `${employeeAttributeId}-${expired ? 'expired' : 'renewed'}`,
      createDate: expired ? attribute.expiresOn : past(shiftDays(attribute.expiresOn, -365)),
      commentReason: expired
        ? 'commentReasons.competencyExpired'
        : 'commentReasons.completedTraining',
      // Always the owner: the feed prints the actor above the reason, and an expiry is a system
      // event — naming a colleague there reads as "Sarah expired David's certification".
      ...actor(owner),
      text: null,
      documentIds: [],
      checklistAssignmentIds: [],
    });
  }

  // The client sorts newest-first itself; order here is chronological for readability.
  return { ...EMPTY_ACTIVITY_HISTORY, activities };
}

function emptySummary() {
  return {
    totalNumberOfMandatoryAttributes: 0,
    numberOfMetMandatoryAttributes: 0,
    totalNumberOfRequiredAttributes: 0,
    numberOfMetRequiredAttributes: 0,
    totalNumberOfDesirableAttributes: 0,
    numberOfMetDesirableAttributes: 0,
  };
}

export const profileRoutes: MockRoute[] = [
  {
    pattern: '/Profile',
    build: ({ dataset, url }) => buildProfile(dataset, url.searchParams.get('employeeId')),
  },
  {
    pattern: '/EmployeeAttribute/GetActivityHistory/:employeeAttributeId',
    build: ({ dataset, params }) => buildActivityHistory(dataset, params.employeeAttributeId),
  },
  // A gap carries `employeeAttributeId: ''` (there is no record yet), which collapses the URL to the
  // bare path — no id segment for the route above to match. Serve the empty feed rather than letting
  // it fail closed: `loadPreviousActivity` has no `.catch()`, so a rejection surfaces as an unhandled
  // promise rejection in the prospect's console.
  { pattern: '/EmployeeAttribute/GetActivityHistory', build: () => EMPTY_ACTIVITY_HISTORY },
  // Profile-dialog Training sub-tab — empty-state (consumed values are empty arrays; see header).
  { pattern: '/EmployeeTraining/GetTrainings', build: () => ({ trainings: [] }) },
  { pattern: '/Profile/Trainings', build: () => ({ trainings: { data: [] } }) },
  { pattern: '/Practical/participant', build: () => ({ practicals: [] }) },
];
